/**
 * QorTrace · Landing — Iter240
 *
 * Tiny scroll-progress indicator pinned to the top of the viewport.
 * Fills left → right as the visitor scrolls the page. Adds a "you're
 * 60% through" wayfinding cue without taking any layout space.
 *
 * Uses requestAnimationFrame so the bar tracks smoothly with momentum
 * scroll on macOS/iOS without scroll-listener thrash.
 */
import React, { useEffect, useRef, useState } from "react";


export default function ScrollProgress() {
  const [pct, setPct] = useState(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    const compute = () => {
      const doc  = document.documentElement;
      const top  = window.scrollY || doc.scrollTop || 0;
      const max  = (doc.scrollHeight || 0) - window.innerHeight;
      if (max <= 0) {
        setPct(0);
        return;
      }
      const p = Math.max(0, Math.min(1, top / max));
      setPct(p);
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      window.requestAnimationFrame(() => {
        compute();
        tickingRef.current = false;
      });
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="landing-scroll-progress"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        zIndex: 60,
        background: "rgba(0,229,255,0.06)",
        pointerEvents: "none",
      }}
    >
      <div
        data-testid="landing-scroll-progress-bar"
        style={{
          width: `${(pct * 100).toFixed(2)}%`,
          height: "100%",
          background:
            "linear-gradient(90deg, rgba(0,229,255,0.4), #00E5FF 60%, rgba(31,221,131,0.85))",
          boxShadow:
            pct > 0.01 ? "0 0 8px rgba(0,229,255,0.55)" : "none",
          transition: "width 80ms linear",
        }}
      />
    </div>
  );
}
